import { useGetUsersByOrg } from "@hooks";
import PersonIcon from "@mui/icons-material/Person";
import {
    Box,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getUserDisplayName, UserPickerOption } from "src/components/UserPicker";
import { UserAvatar } from "src/components/UserAvatar";

type Props = {
    assignee: {
        id: string;
        email: string;
        firstName?: string | null;
        lastName?: string | null;
    } | null;
    onAssigneeChange: (assigneeId: string | null) => Promise<void>;
    disabled?: boolean;
    showName?: boolean;
    disablePortal?: boolean;
};

export const AssigneeButton = ({
    assignee,
    onAssigneeChange,
    disabled = false,
    showName = false,
    disablePortal = true,
}: Props) => {
    const { orgId = "" } = useParams();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const theme = useTheme();

    const { data: orgUsers = [], isLoading: isLoadingUsers } = useGetUsersByOrg({
        orgId,
    });

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        if (!disabled) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectUser = (event: React.MouseEvent, userId: string | null) => {
        event.stopPropagation();
        event.preventDefault();
        handleClose();

        // Don't update if selecting the same user
        if (userId === assignee?.id) return;

        void onAssigneeChange(userId).catch(() => {
            // Errors are handled by the caller (snackbar rollback).
        });
    };

    const avatarSize = 21;
    const fontSize = 9;

    const tooltipTitle = assignee
        ? getUserDisplayName(assignee)
        : "Unassigned - click to assign";
    const displayName = assignee
        ? getUserDisplayName(assignee)
        : "Unassigned";

    const previousAssigneeId = useRef<string | null>(assignee?.id ?? null);

    // Close any lingering menu/backdrop after assignee changes
    useEffect(() => {
        const currentAssigneeId = assignee?.id ?? null;
        if (previousAssigneeId.current !== currentAssigneeId && anchorEl) {
            setAnchorEl(null);
        }
        previousAssigneeId.current = currentAssigneeId;
    }, [assignee?.id, anchorEl]);

    // Stop all events from bubbling to parent (prevents CardActionArea ripple)
    const stopAllPropagation = (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
    };

    return (
        <Box sx={{ position: "relative", display: "inline-flex" }}>
            <Tooltip title={tooltipTitle} placement="top">
                <Box
                    data-testid="assignee-button"
                    className={`flex items-center gap-1 ${disabled ? "" : "cursor-pointer"}`}
                    onClick={handleClick}
                    onMouseDown={stopAllPropagation}
                    onTouchStart={stopAllPropagation}
                    sx={
                        showName
                            ? {
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: "1rem",
                                lineHeight: "20px",
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }
                            : undefined
                    }
                >
                    <UserAvatar
                        userId={assignee?.id}
                        name={assignee ? getUserDisplayName(assignee) : null}
                        size={avatarSize}
                        fontSize={fontSize}
                        icon={<PersonIcon sx={{ fontSize: fontSize + 2 }} />}
                    />
                    {showName && (
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: "inherit",
                                lineHeight: "20px",
                                maxWidth: 160,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {displayName}
                        </Typography>
                    )}
                </Box>
            </Tooltip>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                disablePortal={disablePortal}
                BackdropProps={{
                    onClick: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        handleClose();
                    },
                    onMouseDown: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                    },
                }}
                PaperProps={{
                    sx: {
                        maxHeight: 300,
                        minWidth: 200,
                        overflowY: "auto",
                        boxShadow: 3,
                    },
                }}
                sx={{ zIndex: theme.zIndex.modal + 1 }}
            >
                {isLoadingUsers ? (
                    <MenuItem disabled>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        Loading...
                    </MenuItem>
                ) : ([
                    <MenuItem
                        key="unassigned"
                        onClick={(e) => handleSelectUser(e, null)}
                        selected={!assignee}
                    >
                        <ListItemIcon>
                            <PersonIcon fontSize="small" color="disabled" />
                        </ListItemIcon>
                        <ListItemText
                            primary="Unassigned"
                            primaryTypographyProps={{
                                color: "text.secondary",
                            }}
                        />
                    </MenuItem>,
                    ...orgUsers.map((user) => (
                        <UserPickerOption
                            key={user.id}
                            user={user}
                            selected={user.id === assignee?.id}
                            onClick={(event, selectedUser) =>
                                handleSelectUser(event, selectedUser.id)
                            }
                        />
                    )),
                ])}
            </Menu>
        </Box>
    );
};
